<template>
  <div>
    <v-dialog @keydown.esc="dialogOpen=false" v-model="dialogOpen" persistent max-width="640px">
      <v-card>
        <div class="d-flex justify-end">
          <v-btn
            fab
            small
            color="transparent"
            depressed
            @click="closeDialog()"
            class="mr-1 mt-1"
          >
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </div>
        <v-card-title class="dialogTitle">{{ $t('expenses.bankReconciliation.relations.relatedTransactionsDialog.title') }}</v-card-title>
        <v-card-text class="d-flex flex-column justify-center">
          <div class="text-right">
            <Button @click="openAddRelatedTransactionsDialog()" class="blueButton" v-if="bankReconciliationPermissions['create-related-transaction']">
              <v-icon>mdi-plus</v-icon>
              <span>{{ $t('expenses.bankReconciliation.relations.relatedTransactionsDialog.add') }}</span>
            </Button>
          </div>
          <div v-if="!isEmpty(table.data)">
            <v-data-table
              :loading-text="table.loadingText"
              :loading="table.loading"
              :headers="table.headers"
              :items="table.data"
              :options="table.options"
              hide-default-footer
              class="table"
            >
              <template v-slot:[`item.createdAt`]="{ item }">
                <div>{{ $moment(item.createdAt).format('DD/MM/YYYY') }}</div>
              </template>
              <template v-slot:[`item.description`]="{ item }">
                <div>{{ item.description }}</div>
              </template>
              <template v-slot:[`item.amount`]="{ item }">
                <div class="text-right">{{ item.amount | toFixNumber(2) | numberWithComma | prefix('$') }}</div>
              </template>
              <template v-slot:[`item.updatedBy`]="{ item }">
                <div>{{ updatedBy }}</div>
              </template>
              <template v-slot:[`item.actions`]="{ item }" v-if="bankReconciliationPermissions['delete-related-transaction']">
                <div>
                  <v-btn icon @click="deleteRelatedTransaction(item._id)">
                    <v-icon>mdi-trash-can</v-icon>
                  </v-btn>
                </div>
              </template>
            </v-data-table>
          </div>
          <div v-else class="noInfo mt-2">
            {{ $t('expenses.bankReconciliation.relations.relatedTransactionsDialog.table.noInfo') }}
          </div>
          <p class="sumText">{{ $t('expenses.bankReconciliation.relations.relatedTransactionsDialog.sum', { sum: formatCurrency(amountSum) }) }}</p>
        </v-card-text>
      </v-card>
    </v-dialog>

    <AddRelatedTransactionsViewer
      ref="addRelatedTransactionsViewer"
      v-bind:bankId="bankId"
      v-bind:accountId="accountId"
      @updateRelations="$emit('updateRelations')"
      @updateRelatedTransactions="loadRelatedTransactions()"
    />

    <v-overlay :value="overlay" z-index="99999">
      <v-progress-circular indeterminate size="64"></v-progress-circular>
    </v-overlay>
  </div>
</template>

<script>
import isEmpty from 'lodash/isEmpty'
import sumBy from 'lodash/sumBy'

import formatCurrency from '@/utils/formatCurrency'
import AddRelatedTransactionsViewer from '@/components/modals/AddRelatedTransactionsViewer'
import Button from '@/components/elements/Button'

export default {
  name: 'RelatedTransactionsViewer',
  props: {
    bankId: String,
    accountId: String
  },
  components: {
    Button,
    AddRelatedTransactionsViewer
  },
  emits: ['updateRelations'],
  data () {
    return {
      overlay: false,
      dialogOpen: false,
      table: {
        loadingText: this.$i18n.t('expenses.bankReconciliation.relations.relatedTransactionsDialog.table.loadingText'),
        loading: false,
        headers: [
          { text: this.$i18n.t('expenses.bankReconciliation.relations.relatedTransactionsDialog.table.headers.date'), value: 'createdAt', sortable: false },
          { text: this.$i18n.t('expenses.bankReconciliation.relations.relatedTransactionsDialog.table.headers.description'), value: 'description', sortable: false },
          { text: this.$i18n.t('expenses.bankReconciliation.relations.relatedTransactionsDialog.table.headers.amount'), value: 'amount', sortable: false },
          { text: this.$i18n.t('expenses.bankReconciliation.relations.relatedTransactionsDialog.table.headers.editor'), value: 'updatedBy', sortable: false },
          { text: this.$i18n.t('expenses.bankReconciliation.relations.relatedTransactionsDialog.table.headers.actions'), value: 'actions', sortable: false }
        ],
        data: [],
        total: 0,
        options: {
          groupBy: [],
          groupDesc: [],
          itemsPerPage: -1,
          multiSort: false,
          mustSort: false,
          page: 1,
          sortBy: [],
          sortDesc: []
        }
      },
      relationId: undefined,
      updatedBy: undefined
    }
  },
  watch: {
    dialogOpen: function (val) {
      if (val) {
        this.loadRelatedTransactions()
      }
    }
  },
  computed: {
    amountSum: function () {
      return sumBy(this.table.data, 'amount')
    },
    bankReconciliationPermissions () {
      return this.$store.state.login.permissions['expenses'].components['financial-movements'].actions
    }
  },
  methods: {
    openDialog (relationId) {
      this.relationId = relationId
      this.dialogOpen = true
    },
    closeDialog () {
      this.dialogOpen = false
    },
    loadRelatedTransactions: async function () {
      this.table.loading = true
      try {
        const response = await this.$api.get(`/v1/bank-reconciliation/${this.relationId}/bank-transactions`)
        this.table.data = !isEmpty(response.data) ? response.data[0].bankTransactions : []
        this.updatedBy = (!isEmpty(response.data) && response.data[0].updatedBy) ? response.data[0].updatedBy.email : undefined
      } catch (error) {
        this.$toasted.show(error.message, { duration: 3000 })
      }
      this.table.loading = false
    },
    deleteRelatedTransaction: async function (transactionId) {
      this.overlay = true
      try {
        await this.$api.del(`/v1/bank-reconciliation/${this.relationId}/bank-transactions/${transactionId}`)
        // Reload related transactions
        this.loadRelatedTransactions()
        // Reload relations
        this.$emit('updateRelations')
      } catch (error) {
        this.$toasted.show(error.message, { duration: 3000 })
      }
      this.overlay = false
    },
    formatCurrency,
    openAddRelatedTransactionsDialog () {
      this.$refs.addRelatedTransactionsViewer.openDialog(this.relationId)
    },
    isEmpty
  }
}
</script>

<style lang="scss" scoped>
  .dialogTitle {
    display: flex;
    justify-content: center;
    padding-top: 0 !important;
    font-size: 22px !important;
    color: #01D28E;
  }

  .table {
    overflow-y: auto;
    max-height: 65vh;

    & tr {
      border: 1px solid #ACABAB;
      border-radius: 7px;

      &:first-child {
        border-left: 1px solid #ACABAB;
        border-top-left-radius: 7px;
        border-bottom-left-radius: 7px;
      }
    }

    & td {
      border-top: 1px solid #ACABAB;
      border-bottom: 1px solid #ACABAB;

      &:last-child {
        border-right: 1px solid #ACABAB;
        border-top-right-radius: 7px;
        border-bottom-right-radius: 7px;
      }
    }

    & thead tr:last-child th {
      border-bottom: none !important;
    }
  }

  .sumText {
    margin-top: 23px;
    margin-bottom: 8px;
    font-size: 14px;
    font-weight: 700;
    color: #3F3F3F;
  }

  .noInfo {
    border: 1px solid #ACABAB;
    border-radius: 7px;
    padding-top: 22px;
    padding-bottom: 22px;
    text-align: center;
    line-height: 19px;
    color: #ACABAB;
  }

  .blueButton {
    background-color: #0F6FFF !important;
  }
</style>
